import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import { HOT_KEYWORDS, MEDIA_LIST } from '@/mocks/media';

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = (params.get('q') ?? '').trim();
  const [input, setInput] = useState(q);

  useEffect(() => {
    setInput(q);
    document.title = q ? `${q} - 搜索结果 - 朋友的妈妈在线` : '搜索影视 - 朋友的妈妈在线';
  }, [q]);

  const results = useMemo(() => {
    if (!q) return [];
    const kw = q.toLowerCase();
    return MEDIA_LIST.filter((item) => {
      const haystack = [
        item.title,
        item.director,
        item.region,
        item.year.toString(),
        ...item.genres,
        ...item.tags,
        ...item.cast,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(kw);
    }).sort((a, b) => b.views - a.views);
  }, [q]);

  const submit = (value: string) => {
    const v = value.trim();
    if (!v) return;
    navigate(`/search?q=${encodeURIComponent(v)}`);
  };

  return (
    <div className="w-full">
      <div className="w-full bg-primary-950 text-background-50">
        <div className="px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-background-300 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">搜索结果</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl">影视搜索</h1>

          <form
            onSubmit={(e) => { e.preventDefault(); submit(input); }}
            className="mt-6 flex items-center gap-2 rounded-full bg-background-50 pl-5 pr-1.5 py-1.5 max-w-xl"
          >
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、演员、类型或地区"
              className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            <button type="submit" className="px-5 py-2.5 rounded-full bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap">
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        {q ? (
          <>
            <p className="text-sm text-foreground-600 mb-6">
              关键词「<strong className="text-primary-600">{q}</strong>」共找到 <strong className="text-primary-600">{results.length}</strong> 部作品
            </p>

            {results.length === 0 ? (
              <div className="py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-50">
                <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <p className="text-foreground-700 font-medium">没有找到匹配的影视作品</p>
                <p className="text-sm text-foreground-500 mt-1">换个关键词试试，或浏览下方热门搜索</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {HOT_KEYWORDS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => submit(k)}
                      className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-home-line"></i> 返回首页
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {results.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-50">
            <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <p className="text-foreground-700 font-medium">输入关键词开始搜索</p>
            <p className="text-sm text-foreground-500 mt-1">试试这些热门搜索</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {HOT_KEYWORDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => submit(k)}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}